import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { isEmpty } from 'lodash-es';
import styled from 'styled-components';
import {
  Icons,
  Heading2,
  Heading4,
  FlexBox,
  BaseBox,
  CenterBox,
} from '@docon/docon-ui-library';
import { StyledModal } from './components/StyledModal';
import { getTeleConsultSession } from 'redux/reducer/appointment';
import { colors, borderColor } from 'utilities/colorConfig';
import { Loader } from 'shared';
import { eventService } from 'services';
import { getTimeFromMinutes } from 'utils';
import { cleverTapAnl } from 'utilities';

interface IProps {
  isOpen: boolean;
  paymentID: string;
  paymentStatus: string;
  sourceType?: string;
  onClose: () => void;
}

const Header = styled(FlexBox)`
  height: 100%;
  align-items: center;
`;

const Row = styled(BaseBox)`
  padding: 16px;

  & + & {
    border-top: 1px solid ${borderColor};
  }

  ${FlexBox} {
    padding-top: 10px;
    gap: 10px;
    font-weight: 700;
    align-items: center;
  }

  ${CenterBox} {
    font-weight: 700;
    border-radius: 50%;
    color: ${colors.white};
    background-color:#19deb2;
  }
`;

const Content = styled(BaseBox)`
  margin: 25px 16px 0;
  text-align: left;
  border-radius: 5px;
  background-color: ${colors.white}
`;

const Root = styled(BaseBox)`
  padding: 20px;
  text-align: center;
`;

function PaymentSuccess({
  isOpen,
  paymentID,
  paymentStatus,
  onClose,
  ...props
}: IProps) {

  const isSuccess = paymentStatus === 'SUCCESSFUL' ? true : false;
  const appointmentStatusText = isSuccess ? 'Appointment Booked Successfully' : 'Appointment Booking Failed';
  const dispatch = useDispatch();

  const {
    consultSession,
    isFetchingConsultSession,
  } = useSelector(state => state.appointment);

  useEffect(() => {
    if (paymentStatus != null) {
      dispatch(getTeleConsultSession(paymentID));
    }
  }, [paymentStatus]);

  useEffect(() => {
    if (!isEmpty(consultSession)) {
      triggerEvent('appointment_confirmed');
      triggerEvent('appointment_payment_status');
    }
  }, [consultSession]);

  function renderItem({ firstName, lastName = '', label }: any) {
    if (!firstName) return null;

    return (
      <Row>
        <Heading4 title={label} color={colors.secondaryColor} />
        <FlexBox>
          <CenterBox size={45} >
            {firstName.charAt(0)}{lastName.charAt(0)}
          </CenterBox>
          <Heading4 title={`${firstName} ${lastName}`} color={colors.secondaryColor} />
        </FlexBox>
      </Row>
    );
  }

  const {
    appointment: {
      appointmentDate = '',
      appointmentTime = 0,
      patientDetails = {},
      doctorDetails = {},
    } = {},
    pricing_details = {},
  } = consultSession;

  const displayDate = moment(appointmentDate).add(appointmentTime, 'minutes').format('YYYY-MM-DD');
  const Icon = isSuccess ? Icons.SuccessIcon : Icons.ErrorIcon;

  function triggerEvent(eventName) {
    const followUpAppointment = !!pricing_details?.followup_fee;
    localStorage.removeItem('appointmentTag');
    const event = {
      page_name: 'payment_status',
      slot_type: 'video',
      slot_time: getTimeFromMinutes(appointmentTime),
      doctor_id: doctorDetails.doctorID,
      appointment_id: consultSession?.appointment?.appointmentID,
      teleconsult_id: paymentID,
      appointment_date: displayDate,
      transaction_amount: pricing_details?.total,
      banner_id: '-',
      banner_label: '-',
      is_banner_active: '-',
      followup_transaction_amount: pricing_details?.followup_fee || 0,
      followup_available_flag: followUpAppointment,
      followup_used_flag: followUpAppointment,
    };

    switch (eventName) {
      case 'appointment_confirmed':
        cleverTapAnl.event({
          event_name: eventName,
          event: {
            ...event,
            visit_reason: 'vaccination',
          },
        });
        break;

      case 'appointment_payment_status':
        cleverTapAnl.event({
          event_name: eventName,
          event: {
            ...event,
            patient_id: consultSession?.appointment?.patientID,
            time_to_payment: eventService.endActionDuration('initiatePayment_current_time'),
            payment_mode: '-',
            payment_status: (paymentStatus || '').toLowerCase(),
            payment_gateway: 'paytm',
            payment_response: '-',
          },
        });
        break;
    }
  }

  return (
    <StyledModal
      isOpen={isOpen}
      cancelText={null}
      centered
      title={<Header>Payment Status</Header>}
      $maxHeight={'80vh'}
      $maxWidth={'550px'}
      onOkClick={onClose}
      onClose={onClose}
      {...props}
    >
      {
        isFetchingConsultSession ? <Loader /> : (
          <Root>
            <Icon mb={'24px'} width={60} color={isSuccess ? colors.success : colors.error} />
            <Heading2 pt='6px' title={appointmentStatusText} fontWeight={600} />
            <Heading2 pt='6px' title={displayDate} fontWeight={600} />
            <Heading4 pt='6px' title={'Video Consultation'} color={colors.secondaryColor} />

            <Content>
              {renderItem({ ...patientDetails, label: 'Patient' })}
              {renderItem({ ...doctorDetails, label: 'Doctor' })}
            </Content>
          </Root>
        )
      }
    </StyledModal>
  );
}

export default styled(PaymentSuccess)``;